import { useState } from 'react';
import { toast } from 'sonner';
import { Images } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import { uploadFormImage } from '../../../lib/upload';

/**
 * Bulk "screenshot deck" import: pick any number of images and get one
 * full-page picture slide per file, in selection order. Uploads run
 * sequentially so page order always matches file order; a failure stops
 * the run and reports how far it got rather than skipping pages silently.
 */
export function PicturePagesButton({
  onAdd,
}: {
  onAdd: (urls: string[]) => void;
}) {
  const [progress, setProgress] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (files.length === 0) return;
    const urls: string[] = [];
    try {
      for (const [i, file] of files.entries()) {
        setProgress(`Uploading ${i + 1} of ${files.length}…`);
        urls.push(await uploadFormImage(file));
      }
      onAdd(urls);
      toast.success(`Added ${urls.length} picture page${urls.length === 1 ? '' : 's'}`);
    } catch (err) {
      if (urls.length > 0) onAdd(urls);
      toast.error(
        err instanceof ApiError
          ? `${err.message} — added the first ${urls.length} pages`
          : `Upload failed — added the first ${urls.length} pages`,
      );
    } finally {
      setProgress(null);
    }
  }

  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-token-sm border border-accent/30 bg-accent/10 px-2.5 py-1.5 text-xs text-fg hover:bg-accent/20">
      <Images className="h-3.5 w-3.5 text-fg-muted" />
      {progress ?? 'Picture pages (upload screenshots)'}
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        disabled={progress !== null}
        onChange={(e) => void onPick(e)}
      />
    </label>
  );
}
